import { useMemo } from 'react';
import { gridQueryKey } from '@/shared/hooks';
import type { QueryKey } from '@tanstack/react-query';

type UseGridQueryKeyParams = {
  queryPath: string;
  queryKeyPrefix?: QueryKey;
  providedQueryKey?: QueryKey;
  organisationId?: string | null;
  projectId?: string | null;
  queryInputKey: string;
};

const toQueryKeyArray = (value: QueryKey): QueryKey =>
  (Array.isArray(value) ? value : [value]) as QueryKey;

const buildBaseQueryKey = ({
  prefixArray,
  providedQueryKey,
}: {
  prefixArray: QueryKey;
  providedQueryKey?: QueryKey;
}): QueryKey => {
  if (!providedQueryKey) {
    return prefixArray;
  }
  const providedArray = toQueryKeyArray(providedQueryKey);
  if (providedArray[0] === prefixArray[0] && providedArray[1] === prefixArray[1]) {
    return providedArray;
  }
  return [...prefixArray, ...providedArray] as QueryKey;
};

export const useGridQueryKey = ({
  queryPath,
  queryKeyPrefix,
  providedQueryKey,
  organisationId,
  projectId,
  queryInputKey,
}: UseGridQueryKeyParams): QueryKey => {
  const prefixArray = useMemo(() => {
    const basePrefix = queryKeyPrefix ?? gridQueryKey(queryPath);
    return toQueryKeyArray(basePrefix);
  }, [queryKeyPrefix, queryPath]);

  return useMemo(() => {
    const baseArray = buildBaseQueryKey({ prefixArray, providedQueryKey });
    return [
      ...baseArray,
      organisationId ?? 'personal',
      projectId ?? 'all-projects',
      queryInputKey,
    ] as QueryKey;
  }, [organisationId, prefixArray, projectId, providedQueryKey, queryInputKey]);
};
